import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../context/LanguageContext';

const seoData = {
  en: {
    '/': {
      title: 'Delta Studio Scan Solutions | BIM & LiDAR Services',
      description: 'Advanced point cloud processing, 3D laser scanning, BIM modeling, and digital twin solutions for Construction and Real Estate. Millimeter-accurate LiDAR services.',
      keywords: 'point cloud, LiDAR scanning, BIM modeling, 3D scanning, digital twins, construction technology, real estate scanning, scan to BIM, as-built modeling',
    },
    '/services': {
      title: 'Our Services | Delta Studio Scan Solutions',
      description: 'Comprehensive BIM & LiDAR services including 3D laser scanning, point cloud processing, as-built modeling, digital twins, 3D rendering, managed IT, and cybersecurity solutions.',
      keywords: 'BIM services, LiDAR services, 3D scanning services, point cloud processing, digital twin creation, 3D rendering, managed IT services, cybersecurity',
    },
    '/industries': {
      title: 'Industries We Serve | Delta Studio Scan Solutions',
      description: 'BIM and LiDAR solutions tailored for Construction and Real Estate industries. Reduce costs, minimize rework, and accelerate project delivery.',
      keywords: 'construction BIM, real estate scanning, construction technology, property documentation, pre-construction planning, LiDAR construction',
    },
    '/about': {
      title: 'About Us | Delta Studio Scan Solutions',
      description: 'Leading the digital revolution in Architecture, Engineering, and Construction. Meet our expert team delivering innovative BIM and LiDAR solutions.',
      keywords: 'about Delta Studio, BIM company, LiDAR company, AEC technology, construction innovation, Sergio Ibarra, Erick Ibarra',
    },
    '/contact': {
      title: 'Contact Us | Delta Studio Scan Solutions',
      description: 'Get in touch with Delta Studio Scan Solutions for BIM, LiDAR, point cloud, and IT services. Request a consultation or quote today.',
      keywords: 'contact Delta Studio, BIM consultation, LiDAR quote, point cloud services contact, free consultation',
    },
  },
  es: {
    '/': {
      title: 'Delta Studio Scan Solutions | Servicios BIM y LiDAR',
      description: 'Procesamiento avanzado de nubes de puntos, escaneo láser 3D, modelado BIM y soluciones de gemelos digitales para Construcción e Inmobiliaria.',
      keywords: 'nube de puntos, escaneo LiDAR, modelado BIM, escaneo 3D, gemelos digitales, tecnología de construcción, escaneo inmobiliario',
    },
    '/services': {
      title: 'Nuestros Servicios | Delta Studio Scan Solutions',
      description: 'Servicios integrales de BIM y LiDAR incluyendo escaneo láser 3D, procesamiento de nubes de puntos, modelado as-built, gemelos digitales y renderizado 3D.',
      keywords: 'servicios BIM, servicios LiDAR, escaneo 3D, procesamiento de nube de puntos, gemelos digitales, renderizado 3D, servicios IT, ciberseguridad',
    },
    '/industries': {
      title: 'Industrias | Delta Studio Scan Solutions',
      description: 'Soluciones BIM y LiDAR para las industrias de Construcción e Inmobiliaria. Reduzca costos, minimice retrabajos y acelere la entrega de proyectos.',
      keywords: 'BIM construcción, escaneo inmobiliario, tecnología de construcción, documentación de propiedades, planificación pre-construcción',
    },
    '/about': {
      title: 'Nosotros | Delta Studio Scan Solutions',
      description: 'Liderando la revolución digital en Arquitectura, Ingeniería y Construcción. Conozca a nuestro equipo de expertos en soluciones BIM y LiDAR.',
      keywords: 'sobre Delta Studio, empresa BIM, empresa LiDAR, tecnología AEC, innovación en construcción',
    },
    '/contact': {
      title: 'Contacto | Delta Studio Scan Solutions',
      description: 'Contáctenos para servicios de BIM, LiDAR, nube de puntos y TI. Solicite una consulta o cotización hoy.',
      keywords: 'contacto Delta Studio, consulta BIM, cotización LiDAR, servicios nube de puntos, consulta gratuita',
    },
  },
};

const SEO = ({ path }) => {
  const { lang } = useLanguage();
  const data = seoData[lang]?.[path] || seoData.en['/'];
  const canonicalUrl = `https://www.delta3dscan.com${path === '/' ? '' : path}`;
  const logoUrl = 'https://customer-assets.emergentagent.com/job_bimlidar-experts/artifacts/d5qki862_3D_logo_-_white_Background_200x200-removebg-preview.png';

  return (
    <Helmet>
      <title>{data.title}</title>
      <meta name="description" content={data.description} />
      <meta name="keywords" content={data.keywords} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content="index, follow" />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={data.title} />
      <meta property="og:description" content={data.description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="Delta Studio Scan Solutions" />
      <meta property="og:image" content={logoUrl} />
      <meta property="og:locale" content={lang === 'es' ? 'es_US' : 'en_US'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={data.title} />
      <meta name="twitter:description" content={data.description} />
      <meta name="twitter:image" content={logoUrl} />

      {/* Language */}
      <html lang={lang} />
    </Helmet>
  );
};

export default SEO;
